"use client";

import { useState, ReactNode } from "react";
import VideoModal from "@components/molecules/VideoModal/VideoModal";
import VideoCarouselCardClientWrapper from "@components/molecules/VideoCarouselCard/VideoCarouselCardClientWrapper";
import { DeviceType, VideoMetadata, VideoSource } from "@interfaces/video";
import { createVideoSource } from "@utils/videoUtils";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";

export const VideoCarousalClient = ({
  children,
  videoData,
  cityName,
  brandName,
  modelName,
  variantName,
  carAvailability,
  pageType,
}: {
  children: ReactNode;
  videoData: any;
  cityName?: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  carAvailability?: string;
  pageType?: string;
}) => {
  const [selectedVideo, setSelectedVideo] = useState<VideoSource | null>(null);
  const [selectedMetadata, setSelectedMetadata] =
    useState<VideoMetadata | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [deviceType] = useState<DeviceType>(DeviceType.DESKTOP);

  const handleVideoClick = async (url: string, metadata: VideoMetadata) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_Youtube_video",
        video_locked: metadata?.isExclusive ? "Yes" : "No",
        video_tag: "RELATED",
        video_title: metadata?.title || "",
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        ...(variantName && { vehicle_variant: variantName }),
      },
    });

    setSelectedVideo(createVideoSource(url));
    setSelectedMetadata(metadata);
    setIsModalOpen(true);
  };

  const handleModalClose = async () => {
    if (selectedMetadata) {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_close_Youtube_video",
          video_tag: selectedMetadata?.title || "",
          video_title: selectedMetadata?.title || "",
          closing_time: new Date().toISOString(),
          car_availability: carAvailability,
          city_name: cityName,
          vehicle_make: brandName,
          vehicle_model: modelName,
        },
      });
    }

    setIsModalOpen(false);
    setSelectedVideo(null);
    setSelectedMetadata(null);
  };

  return (
    <>
      <VideoCarouselCardClientWrapper
        onClick={() => handleVideoClick(videoData?.url, videoData?.metadata)}
      >
        {children}
      </VideoCarouselCardClientWrapper>

      <VideoModal
        video={selectedVideo}
        metadata={selectedMetadata}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        deviceType={deviceType}
        config={{
          autoplay: true,
          muted: false,
          showTitle: true,
          showProgress: true,
          showVolume: true,
          showClose: true,
        }}
        events={{
          onPlay: () => console.log("Video started playing"),
          onPause: () => console.log("Video paused"),
          onEnded: () => console.log("Video ended"),
          onError: (error) => console.error("Video error:", error),
        }}
      />
    </>
  );
};
